import { CheckCircle, ExternalLink } from 'lucide-react'
import { FaTwitter } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type { TwitterPostResult } from 'sprites-types/workflow/tools/twitterPost'
import { twitterPost } from 'sprites-types/workflow/tools/twitterPost'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function TwitterPostDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(twitterPost, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsedResult = parseResult.data
  const posts: TwitterPostResult[] = Array.isArray(parsedResult) ? parsedResult : [parsedResult]
  const inputs = nodeExecution.inputs

  const tweetText = inputs.text || inputs.content || 'Tweet content'
  const isMulti = posts.length > 1
  const first = posts[0]
  const tweetUrl = first?.url ?? ''
  const tweetId = first?.tweetId ?? ''

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-blue-100 p-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <FaTwitter className="h-4 w-4 text-blue-500" />
            <div className="text-sm font-semibold text-gray-900">Tweet posted successfully</div>
          </div>

          <div className="mb-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
            {isMulti ? (
              <div className="space-y-2">
                {posts.map((p) => (
                  <div
                    key={p.tweetId}
                    className="rounded-xl border border-gray-200 bg-white/70 p-2"
                  >
                    <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                      <div>ID: {p.tweetId}</div>
                      {p.url && (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View
                        </a>
                      )}
                    </div>
                    <div className="line-clamp-3 whitespace-pre-wrap text-sm text-gray-800">
                      {p.content || tweetText}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="line-clamp-4 whitespace-pre-wrap text-sm text-gray-800">
                {first?.content || tweetText}
              </div>
            )}
          </div>

          {!isMulti && (
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {tweetId && <div>ID: {tweetId}</div>}
              {tweetUrl && (
                <a
                  href={tweetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  View on Twitter
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadTwitterPost(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function twitterPostToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = nodeExecution.inputs
  const schema = getToolResultSchema(twitterPost, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const parsedResult = parseResult.data
  const posts: TwitterPostResult[] = Array.isArray(parsedResult) ? parsedResult : [parsedResult]

  const tweetText = inputs.text || inputs.content || 'Tweet content'
  const isMulti = posts.length > 1

  if (isMulti) {
    const lines = posts
      .map(
        (p) =>
          `- ${p.content || tweetText}${p.url ? ` (\u005BView on Twitter\u005D(${p.url}))` : ''}`
      )
      .join('\n')
    return `## Tweets Posted Successfully

${lines}

---
`
  }

  const only = posts[0]
  const tweetUrl = only?.url || ''

  return `## Tweet Posted Successfully

${tweetText}

${tweetUrl ? `[View on Twitter](${tweetUrl})` : ''}

---
`
}

export const TwitterPostDisplay = createWorkflowToolComponent(
  TwitterPostDisplayComponent,
  downloadTwitterPost,
  twitterPostToMarkdown
)
